/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * (Theme documentation unchanged. See your original file for the full comment block covering variant events,
 *  custom elements, drawers, and theme dependencies.)
 */

/* ============================================================================
 * GA4 PRODUCT IMPRESSION TRACKING (view_item_list)
 *
 * Design:
 *  - Cards are counted based on SCROLL SPEED, not dwell time. If the user is
 *    scrolling slowly (browsing), a card crossing 50% visibility is counted.
 *    If they are scrolling fast (flinging past), it is skipped.
 *  - Counted cards are pushed onto a stack. Every time the stack reaches 10,
 *    that group of 10 is sent as one view_item_list event and the stack clears.
 *  - No per-card dwell timer and no idle-flush timer.
 *  - A lightweight "scroll settled" check resets the fast flag when scrolling
 *    stops, and sweeps in any card that was skipped mid-fling but is still on
 *    screen once the user stops on it.
 *  - Partial groups (fewer than 10) are only sent on teardown (pagehide /
 *    tab hidden), so during normal browsing sends always happen in tens.
 * ========================================================================== */

const GROUP_SIZE = 5;

// Pixels per millisecond. Above this the user is considered to be scrolling
// fast and cards are not counted. Deliberate reading scroll is roughly
// 0.3-0.6 px/ms; a quick flick is 2+ px/ms. Tune to taste.
const FAST_SCROLL_THRESHOLD = 1.2;

// How long with no scroll events before we treat scrolling as "settled".
// This is a scroll-end detector, not a per-card timer.
const SCROLL_SETTLE_DELAY = 150;

// The stack of pending items waiting to be sent.
let observedProductCards = [];

// Cards currently intersecting the viewport.
const visibleProducts = new Set();

// Cards that entered while the user was scrolling fast, so were not counted.
// Re-checked when scrolling settles, in case the user stopped on one.
const skippedWhileFast = new Set();

// Scroll-speed state.
let lastScrollY = window.scrollY;
let lastScrollTime = performance.now();
let isFastScroll = false;
let scrollEndTimeout;

const options = {
  root: null,
  rootMargin: "0px",
  threshold: 0.5,
};

const observer = new IntersectionObserver(callback, options);

// --- Scroll speed measurement -------------------------------------------------

window.addEventListener(
  "scroll",
  () => {
    const now = performance.now();
    const currentY = window.scrollY;
    const dt = now - lastScrollTime;

    if (dt > 0) {
      const speed = Math.abs(currentY - lastScrollY) / dt; // px per ms
      isFastScroll = speed >= FAST_SCROLL_THRESHOLD;
    }

    lastScrollY = currentY;
    lastScrollTime = now;

    clearTimeout(scrollEndTimeout);
    scrollEndTimeout = setTimeout(onScrollSettled, SCROLL_SETTLE_DELAY);
  },
  { passive: true }
);

function onScrollSettled() {
  isFastScroll = false;

  // The user has stopped. Any card that was skipped during a fast scroll but
  // is still on screen is now something they are actually looking at, so count it.
  skippedWhileFast.forEach((card) => {
    if (visibleProducts.has(card)) {
      bufferProduct(card);
    }
  });
  skippedWhileFast.clear();
}

// --- Intersection handling ----------------------------------------------------

function callback(entries) {
  entries.forEach((entry) => {
    const card = entry.target;

    if (entry.isIntersecting) {
      visibleProducts.add(card);

      if (isFastScroll) {
        // Whipping past. Do not count, but remember it in case they stop on it.
        skippedWhileFast.add(card);
        return;
      }

      bufferProduct(card);
    } else {
      visibleProducts.delete(card);
      skippedWhileFast.delete(card);
    }
  });
}

// Push one card onto the stack and flush if we have reached a full group.
function bufferProduct(card) {
  skippedWhileFast.delete(card);

  observedProductCards.push({
    item_id: card.getAttribute("data-product-id"),
    item_name: card.getAttribute("data-product-name"),
    index: card.getAttribute("product-index-number"),
    item_brand: card.getAttribute("data-product-vendor"),
    item_list_id: card.getAttribute("data-collection-id"),
    item_list_name: card.getAttribute("data-product-collection"),
    item_variant: card.getAttribute("data-product-variant"),
    price: card.getAttribute("data-product-price"),
    quantity: 1,
  });

  if (observedProductCards.length >= GROUP_SIZE) {
    flushGroups();
  }
}

// --- Sending ------------------------------------------------------------------

// Send every full group of 10. When force is true (teardown), also send
// whatever partial group is left over.
function flushGroups(force = false) {
  while (observedProductCards.length >= GROUP_SIZE) {
    sendGroup(observedProductCards.splice(0, GROUP_SIZE));
  }

  if (force && observedProductCards.length > 0) {
    sendGroup(observedProductCards.splice(0, observedProductCards.length));
  }
}

function sendGroup(items) {
  if (items.length === 0) return;

  const last = items[items.length - 1];

  gtag("event", "view_item_list", {
    send_to: ["G-RRPQVQGM50"],
    item_list_id: last.item_list_id,
    item_list_name: last.item_list_name,
    items: items,
  });
}

// --- Observing cards (including ones loaded later) ----------------------------

function observeProductItems(items) {
  items.forEach((productItem) => {
    if (productItem.dataset.gaObserved) return; // already observed
    productItem.dataset.gaObserved = "1";
    observer.observe(productItem);
  });
}

observeProductItems(document.querySelectorAll(".product-item"));

// Collection pages often load more cards on scroll (infinite scroll / load more).
// Observe any new .product-item cards as soon as they are added to the DOM.
const productGridObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;

      if (node.matches && node.matches(".product-item")) {
        observeProductItems([node]);
      }
      if (node.querySelectorAll) {
        observeProductItems(node.querySelectorAll(".product-item"));
      }
    });
  });
});

productGridObserver.observe(document.body, { childList: true, subtree: true });

// Send any leftover partial group when the user leaves or backgrounds the page.
window.addEventListener("pagehide", () => flushGroups(true));
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    flushGroups(true);
  }
});

/* ============================================================================
 * GA4 select_item TRACKING (product card clicks) — unchanged
 * ========================================================================== */

document.addEventListener("DOMContentLoaded", function () {
  let collectionItems = document.querySelectorAll(".merca_plp_click");

  const handleClick = function () {
    const productName = this.getAttribute("data-product-name");
    const productId = this.getAttribute("data-product-id");
    const productVendor = this.getAttribute("data-product-vendor");
    const productVariant = this.getAttribute("data-product-variant");
    const productPrice = this.getAttribute("data-product-price");
    const productCollection = this.getAttribute("data-product-collection");
    const CollectionId = this.getAttribute("data-collection-id");
    const indexNumber = this.getAttribute("product-index-number");
    gtag("event", "select_item", {
      send_to: ["G-RRPQVQGM50"],
      item_list_id: CollectionId ? CollectionId : "home",
      item_list_name: productCollection ? productCollection : "home",
      items: [
        {
          item_id: productId,
          item_name: productName,
          index: indexNumber,
          item_brand: productVendor,
          item_list_id: CollectionId ? CollectionId : "home",
          item_list_name: productCollection ? productCollection : "home",
          item_variant: productVariant,
          price: productPrice,
          quantity: 1,
        },
      ],
    });
  };

  const attachEventListeners = (items) => {
    Array.from(items).forEach(function (element) {
      if (element.dataset.gaClickBound) return; // already bound
      element.dataset.gaClickBound = "1";
      element.addEventListener("click", handleClick);
    });
  };

  attachEventListeners(collectionItems);

  // Bind click tracking on any .merca_plp_click cards added later too.
  const clickTargetObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;

        if (node.matches && node.matches(".merca_plp_click")) {
          attachEventListeners([node]);
        }
        if (node.querySelectorAll) {
          attachEventListeners(node.querySelectorAll(".merca_plp_click"));
        }
      });
    });
  });

  clickTargetObserver.observe(document.body, { childList: true, subtree: true });
});